import { IComponentOptions, IOnChangesObject } from 'angular';
import { StateService, Transition, TransitionService } from 'angular-ui-router';
import {
    FONT_POSTURE_OPTIONS,
    FONT_WEIGHT_OPTIONS,
    FONT_WIDTH_OPTIONS,
    FontFamilyService,
    FontListParams,
    FontListParamsStateParamsResolver,
    FontListParamsToStateParams,
    FontPosture,
    FontStyleService,
    FontWeight,
    FontWidth,
    IFontFamilyModel,
    IFoundryModel,
    IPeopleModel,
    Option,
} from '../../../tn.common';
import { ISeries } from '../../../tn.fonts/SeriesService';
import { PeopleService } from '../../../tn.fonts/tn.fonts.service/people';
import template from './series-page-sidebar.html';

export class SeriesPageSidebarController {
    // bindings
    filters: FontListParams = {};
    series?: ISeries;
    foundry?: IFoundryModel;

    // locals
    loaded = false;
    designers: IPeopleModel[] = [];
    families: IFontFamilyModel[] = [];
    familyOptions: Option<string>[] = [];
    postures: Option<FontPosture>[] = [];
    weights: Option<FontWeight>[] = [];
    widths: Option<FontWidth>[] = [];

    static $inject = [
        '$state',
        '$transitions',
        'FontFamilyPublicService',
        'FontStylePublicService',
        'tn.people.service',
    ];

    constructor(
        protected $state: StateService,
        protected $transitions: TransitionService,
        protected familyService: FontFamilyService,
        protected styleService: FontStyleService,
        protected peopleService: PeopleService
    ) {
        const onTransitionSuccess = (transition: Transition) => {
            const params = transition.params();
            // common font filters.
            const filters = FontListParamsStateParamsResolver(params);
            // view specific family_slug_filter.
            const family = params.family || '';
            filters.family_slug =
                family
                    .split(',')
                    .map((s: string) => s.trim())
                    .filter(Boolean) || [];
            this.filters = filters;
        };
        $transitions.onSuccess(
            { to: 'home.foundry.series' },
            onTransitionSuccess
        );
    }

    $onChanges(changes: IOnChangesObject): void {
        if (
            changes.series &&
            changes.series.currentValue != changes.series.previousValue
        ) {
            this.onSeriesChanged();
        }
    }
    onSeriesChanged(): void {
        const series = this.series;
        const familyIds = this.series?.family;
        if (!series || !familyIds || familyIds.length === 0) {
            this.loaded = true;
            this.families = [];
            this.familyOptions = [];
            return;
        }

        this.peopleService.listPeople().then((people) => {
            this.designers = series.designers.map(
                (designer) => people[designer]
            );
        });

        this.familyService
            .list({ id: familyIds, page_size: 10000 })
            .then(({ results }) => {
                this.families = results.sort((a, b) => {
                    const diff =
                        Math.max.apply(null, b.recommended_size) -
                        Math.max.apply(null, a.recommended_size);
                    if (diff === 0) {
                        return a.name < b.name ? -1 : b.name < a.name ? 1 : 0;
                    }
                    return diff;
                });

                this.familyOptions = this.families.map((family) => ({
                    label: family.name,
                    value: family.slug,
                }));
                this.postures = FONT_POSTURE_OPTIONS.filter((option) =>
                    results.some((family) =>
                        family.posture.includes(option.value)
                    )
                );
                this.weights = FONT_WEIGHT_OPTIONS.filter((option) =>
                    results.some((family) =>
                        family.weight.includes(option.value)
                    )
                );
                this.widths = FONT_WIDTH_OPTIONS.filter((option) =>
                    results.some((family) =>
                        family.width.includes(option.value)
                    )
                );
            });
    }

    modifyState(params: Partial<FontListParams & { family: string[] }>): void {
        const stateParams = FontListParamsToStateParams({
            ...this.filters,
            ...params,
        });

        if (params.family) {
            stateParams.family = params.family.join(',');
        }
        // console.log('SeriesPageSidebarController.modifyState', stateParams);
        this.$state.go('.', stateParams);
    }

    clearFilters(): void {
        this.filters.family = [];
        this.$state.go(
            '.',
            {
                foundryEESubdomain: this.foundry?.ee_subdomain,
                seriesName: this.series?.slug,
                family: undefined,
            },
            { inherit: false }
        );
    }
    hasFilter(key: keyof FontListParams, value: unknown): boolean {
        const filter = this.filters[key];
        if (filter == undefined) return false;
        if (value == undefined) {
            return Array.isArray(filter) ? filter.length > 0 : filter !== '';
        }
        return Array.isArray(filter)
            ? (filter as unknown[]).includes(value)
            : filter === value;
    }
}

export const SeriesPageSidebarComponent: IComponentOptions = {
    bindings: {
        filters: '<',
        series: '<',
        foundry: '<',
    },
    controller: SeriesPageSidebarController,
    controllerAs: 'vm',
    template,
};
